import {deepAssign} from '@/hosted_fields/common/dom-utils';
import Sanitizer, {addStyle} from '@/hosted_fields/common/sanitizer';
import {
  FontFace,
  FontURL,
  ComponentOptions as Options,
  FieldComponentOptions,
  InputFieldOptions,
  Styles,
  StyleBlock,
  Locale,
} from '@/hosted_fields/common/types';
import {Field, Card, HostedFields, Entity, IDeal, CardIconPosition} from '@/hosted_fields/common/enums';
import {t, TextCodes} from '@/hosted_fields/common/locale';

const INNER_CC_FIELD = (option: InputFieldOptions) => `
  <span>
    <label class="CardData" data-mxw="${option.mxw}">
      <input type="tel" class="CardInput ${HostedFields.InternalCSSClass.empty}" id="${option.name}" name="${option.fieldDesc}" maxlength="${option.maxlength}"  autocomplete="${option.autocomplete}" placeholder="${option.placeholder}" autocorrect="off" autocapitalize="none" spellcheck="false" aria-describedby="${option.describedby}" inputmode="numeric" aria-label="${option.ariaLabel}"> 
      <input type="tel" name="card" style="position: absolute; visibility: hidden;" autocomplete="cc-csc">
    </label>
  </span>
`;

export function getPlaceholderText(
  options: FieldComponentOptions,
  fieldType: Card.ComponentFieldType | IDeal.ComponentFieldType
) {
  const {component = {}, field = {}} = options;
  return Sanitizer.htmlSafeText(
    field.placeholder ||
      (component.placeholder && component.placeholder[fieldType]) ||
      t(TextCodes.placeholder[fieldType], null, component.locale as Locale)
  );
}

export function getAriaLabelText(
  options: FieldComponentOptions,
  fieldType: Card.ComponentFieldType | IDeal.ComponentFieldType
) {
  const {component = {}, field = {}} = options;
  return Sanitizer.htmlSafeText(
    field.ariaLabel ||
      (component.ariaLabel && component.ariaLabel[fieldType]) ||
      t(TextCodes.ariaLabel[fieldType], null, component.locale as Locale)
  );
}

const INNER_CC_NUMBER = (option: FieldComponentOptions) =>
  INNER_CC_FIELD({
    fieldDesc: 'cardnumber',
    mxw: '4088 8080 8080 8080 888',
    name: Field.Number,
    autocomplete: 'cc-number',
    maxlength: 22,
    placeholder: getPlaceholderText(option, Card.ComponentFieldType.Number),
    describedby: 'field-description-number',
    ariaLabel: getAriaLabelText(option, Card.ComponentFieldType.Number),
  });

const INNER_CC_EXPIRY = (option) =>
  INNER_CC_FIELD({
    fieldDesc: 'cardnumber-expiry',
    mxw: 'MM / YYYY',
    name: Field.Expiry,
    autocomplete: 'cc-exp',
    maxlength: 7,
    placeholder: getPlaceholderText(option, Card.ComponentFieldType.Expiry),
    describedby: 'field-description-expiry',
    ariaLabel: getAriaLabelText(option, Card.ComponentFieldType.Expiry),
  });

const INNER_CC_CVV = (option) =>
  INNER_CC_FIELD({
    fieldDesc: 'cvv',
    mxw: '8000',
    name: Field.CVV,
    autocomplete: 'cc-csc',
    maxlength: 4,
    placeholder: getPlaceholderText(option, Card.ComponentFieldType.CVV),
    describedby: 'field-description-cvv',
    ariaLabel: getAriaLabelText(option, Card.ComponentFieldType.CVV),
  });

const WRAPPER = (template: string, option: FieldComponentOptions) => `
  <div id="root" class="CardHosted">
    <form class="CardForm">
      ${styleGenerator(option)}
      ${template}
    </form>
  </div>
`;

export const styleGenerator = (options: FieldComponentOptions): string => {
  const componentStyles: Styles = (options.component || {}).style;
  const fieldStyles: Styles = (options.field || {}).style;
  const styles: Styles = deepAssign({}, componentStyles, fieldStyles);

  const baseStyles: StyleBlock = styles.base || {};
  const invalidStyles: StyleBlock = styles.invalid || {};

  let {
    color,
    iconColor,
    fontSize,
    fontWeight,
    fontFamily,
    fontSmoothing,
    letterSpacing,
    textAlign,
    textTransform,
    textDecoration,
    textShadow,
    fontVariant,
    lineHeight,
  } = baseStyles;

  // Pseudo elements
  // Placeholder
  const placeholder = baseStyles['::placeholder'];
  let placeholderColor = '';
  let placeholderFontSize = '';
  if (placeholder) {
    placeholderColor = placeholder.color;
    placeholderFontSize = placeholder.fontSize || fontSize;
  }

  // Webkit autofill
  const autofill = baseStyles[':-webkit-autofill'];
  let autofillColor = '';
  let autofillWebkitColor = '';
  if (autofill) {
    autofillColor = autofill.color || color;
    autofillWebkitColor = autofill.webkitTextColor || color;
  } else {
    // Adds base style color
    autofillColor = color;
    autofillWebkitColor = color;
  }

  //Selection
  const selection = baseStyles['::selection'];
  let selectionColor = '';
  let selectionBackgroundColor = '';
  if (selection) {
    selectionColor = selection.color || selectionColor;
    selectionBackgroundColor = selection.background || selection.backgroundColor || selectionBackgroundColor;
  }

  //Focus
  const focus = baseStyles[':focus'];
  let focusFontSize = '';
  let focusColor = '';
  let focusPlaceholderColor = '';
  let focusIconColor = '';
  if (focus) {
    focusIconColor = focus.iconColor || focusIconColor;
    focusFontSize = focus.fontSize || focusFontSize;
    focusColor = focus.color || focusColor;
    focusPlaceholderColor =
      focus['::placeholder'] && focus['::placeholder'].color
        ? focus['::placeholder'].color
        : (baseStyles[':focus::placeholder'] && baseStyles[':focus::placeholder'].color) || placeholderColor;
  }

  // Create Style Element
  // Overflow hidden added to fix https://github.com/chargebee/chargebee-js-wrappers/issues/70
  return `
  <style>
    .CardForm,
    .CardForm .CardInput{
      overflow: hidden;
      ${addStyle('color', color)}
      ${addStyle('font-size', fontSize)}
      ${addStyle('font-weight', fontWeight)}
      ${addStyle('font-family', fontFamily)}
      ${addStyle('font-variant', fontVariant)}
      ${
        fontSmoothing
          ? `
        -webkit-font-smoothing: ${fontSmoothing};
        -moz-osx-font-smoothing: grayscale;`
          : ''
      }
      ${addStyle('letter-spacing', letterSpacing)}
      ${addStyle('text-align', textAlign)}
      ${addStyle('text-transform', textTransform)}
      ${addStyle('text-decoration', textDecoration)}
      ${addStyle('text-shadow', textShadow)}
      ${addStyle('line-height', lineHeight)}
    }
    .CardForm .CardIcon-fill{
      ${addStyle('fill', iconColor)}
    }
    .CardForm .CardInput.${HostedFields.InternalCSSClass.focus} {
      ${addStyle('color', focusColor)}
    }

    .CardForm .CardInput::-webkit-input-placeholder {
      ${addStyle('color', placeholderColor)} 
      ${addStyle('font-size', placeholderFontSize)}
      opacity: 1;      
    }
    .CardForm .CardInput::-moz-placeholder {
      ${addStyle('color', placeholderColor)} 
      ${addStyle('font-size', placeholderFontSize)}
      opacity: 1;   
    }
    .CardForm .CardInput:-ms-input-placeholder {
      ${addStyle('color', placeholderColor)} 
      ${addStyle('font-size', placeholderFontSize)}
      opacity: 1;     
    }
    .CardForm .CardInput::placeholder {
      ${addStyle('color', placeholderColor)} 
      ${addStyle('font-size', placeholderFontSize)}
      opacity: 1;    
    }

    .CardForm .CardInput:-webkit-autofill {
      ${addStyle('color', autofillColor)}
      ${addStyle('-webkit-text-fill-color', autofillWebkitColor)}
    }
    
    .CardForm.${HostedFields.InternalCSSClass.focus} .CardIcon-fill{
      ${addStyle('fill', focusIconColor)}
    }

    .CardForm .CardInput.${HostedFields.InternalCSSClass.focus}::-webkit-input-placeholder {
      ${addStyle('color', focusPlaceholderColor)} 
      opacity: 1;      
    }
    .CardForm .CardInput.${HostedFields.InternalCSSClass.focus}::-moz-placeholder {
      ${addStyle('color', focusPlaceholderColor)} 
      opacity: 1;   
    }
    .CardForm .CardInput.${HostedFields.InternalCSSClass.focus}:-ms-input-placeholder {
      ${addStyle('color', focusPlaceholderColor)} 
      opacity: 1;     
    }
    .CardForm .CardInput.${HostedFields.InternalCSSClass.focus}::placeholder {
      ${addStyle('color', focusPlaceholderColor)} 
      opacity: 1;    
    }

    .CardForm.${HostedFields.InternalCSSClass.invalid} .CardIcon-fill{
      ${addStyle('fill', invalidStyles.iconColor)}
    }

    .CardForm .CardInput.${HostedFields.InternalCSSClass.invalid} {
      ${addStyle('color', invalidStyles.color)}
    }

    .CardForm .CardInput.${HostedFields.InternalCSSClass.invalid}.${HostedFields.InternalCSSClass.focus} {
      ${addStyle('color', invalidStyles[':focus'] && invalidStyles[':focus'].color)}
    }
    
    .CardForm .CardInput.${HostedFields.InternalCSSClass.invalid}::-webkit-input-placeholder {      
      ${addStyle('color', invalidStyles['::placeholder'] && invalidStyles['::placeholder'].color)}
      opacity: 1;
    }
    .CardForm .CardInput.${HostedFields.InternalCSSClass.invalid}::-moz-placeholder {
      ${addStyle('color', invalidStyles['::placeholder'] && invalidStyles['::placeholder'].color)}
      opacity: 1;
    }
    .CardForm .CardInput.${HostedFields.InternalCSSClass.invalid}:-ms-input-placeholder {
      ${addStyle('color', invalidStyles['::placeholder'] && invalidStyles['::placeholder'].color)}
      opacity: 1;
    }
    .CardForm .CardInput.${HostedFields.InternalCSSClass.invalid}::placeholder {
      ${addStyle('color', invalidStyles['::placeholder'] && invalidStyles['::placeholder'].color)}
      opacity: 1;
    }    
    .CardForm .CardInput::selection {
      ${addStyle('color', selectionColor)}
      ${addStyle('background-color', selectionBackgroundColor)}
    }
  </style>
  `;
};

const CardIconTemplate = (options: FieldComponentOptions): string => `
  <div class="CardIcon CardIcon-number ${
    !options.component[HostedFields.Options.icon] ? 'CardIcon--hidden' : ''
  } CardIcon--loaded CardIcon--front">
  <div class="CardIcon-contain">
    <div class="CardIcon-inner ${
      options.cardIconPosition == CardIconPosition.START ? 'CardIcon-inner-start' : ''
    } CardIcon-front" id="CardIcon-parent">
    ${CardIconSVG}
    </div>
    <div class="CardIcon-inner CardIcon-back">
    ${CardIconBackSVG}
    </div>
  </div>
  </div>
`;

// !TODO we need to sanitize all HTML content before we insert into the DOM
export const CardCombinedTemplate = (option: FieldComponentOptions): string =>
  WRAPPER(
    `
  <div class="CardWrap CardWrap--ltr">
    ${CardIconTemplate(option)}
    <div class="CardFields">
      <span class="CardField CardField--number">
        <span class="CardNumberClone">
          <span class="CardNumberClone-last CardInput" id="cc_clone_last_group"></span>
          <span class="CardNumberClone-full" id="cc_clone_number"></span>
        </span>
        ${INNER_CC_NUMBER(option)}
      </span>
      <span class="CardField CardField--expiry">
        ${INNER_CC_EXPIRY(option)}
      </span>
      <span class="CardField CardField--cvc">
        ${INNER_CC_CVV(option)}
      </span>
    </div>
  </div>
`,
    option
  );

export const CardNumberTemplate = (option: FieldComponentOptions = {}): string =>
  WRAPPER(
    `
  <div class="CardWrap CardWrap--rtl">
  ${CardIconTemplate(option)}
  <div class="CardFields">
    ${INNER_CC_NUMBER(option)}
  </div>
  </div>
`,
    option
  );

export const CardExpiryTemplate = (option: FieldComponentOptions = {}): string =>
  WRAPPER(
    `
  ${INNER_CC_EXPIRY(option)}
`,
    option
  );

export const CardCVVTemplate = (option: FieldComponentOptions = {}): string =>
  WRAPPER(
    `
  ${INNER_CC_CVV(option)}
`,
    option
  );

export const FontTemplate = (fonts: FontFace[], whitelist: Array<string>): string => {
  let parent = {fonts};
  // Sanitize fonts
  Sanitizer[HostedFields.Options.fonts](parent.fonts, parent, Entity.component, whitelist);

  let fontURLs: string[] = [];
  let fontFaces: FontFace[] = [];
  fonts.map((font) => {
    if (typeof font === 'string') fontURLs.push(font);
    else fontFaces.push(font);
  });

  let fontURLTemplate = fontURLs
    .map((url) => `<link rel="stylesheet" href="${url}">`)
    .join('\n')
    .trim();
  let fontTemplates = fontFaces
    .map(
      ({fontFamily, fontStyle, fontWeight, src}) => `
    @font-face {
      ${addStyle('font-family', fontFamily)}
      ${addStyle('font-style', fontStyle)}
      ${addStyle('font-weight', fontWeight)}
      ${addStyle('src', src)}
    }
  `
    )
    .join('\n')
    .trim();
  return `
    ${fontURLTemplate ? fontURLTemplate : ''}
    ${fontTemplates ? `<style>${fontTemplates}</style>` : ''}
  `.trim();
};

/* SVG Icons */
export const CardIconSVG = `
<svg id="CardIcon-svg" focusable="false" viewBox="0 0 80 54" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
        <g id="Desktop" transform="translate(-262.000000, -835.000000)" class="CardIcon-fill">
            <g id="card-new4" transform="translate(262.000000, 835.000000)">
                <g id="card-alert-copy">
                    <path d="M6,0 L72,8.8817842e-16 C75.3137085,2.79460044e-16 78,2.6862915 78,6 L78,46 C78,49.3137085 75.3137085,52 72,52 L6,52 C2.6862915,52 1.37284885e-14,49.3137085 1.33226763e-14,46 L1.33226763e-14,6 C1.2916864e-14,2.6862915 2.6862915,1.4968968e-15 6,8.8817842e-16 Z M9,17 C7.34314575,17 6,18.3431458 6,20 C6,21.6568542 7.34314575,23 9,23 L17,23 C18.6568542,23 20,21.6568542 20,20 C20,18.3431458 18.6568542,17 17,17 L9,17 Z M26,17 C24.3431458,17 23,18.3431458 23,20 C23,21.6568542 24.3431458,23 26,23 L34,23 C35.6568542,23 37,21.6568542 37,20 C37,18.3431458 35.6568542,17 34,17 L26,17 Z M43,17 C41.3431458,17 40,18.3431458 40,20 C40,21.6568542 41.3431458,23 43,23 L51,23 C52.6568542,23 54,21.6568542 54,20 C54,18.3431458 52.6568542,17 51,17 L43,17 Z M60,17 C58.3431458,17 57,18.3431458 57,20 C57,21.6568542 58.3431458,23 60,23 L68,23 C69.6568542,23 71,21.6568542 71,20 C71,18.3431458 69.6568542,17 68,17 L60,17 Z M9.32432432,30.6521739 C7.66747007,30.6521739 6.32432432,31.9953197 6.32432432,33.6521739 C6.32432432,35.3090282 7.66747007,36.6521739 9.32432432,36.6521739 L31.3243243,36.6521739 C32.9811786,36.6521739 34.3243243,35.3090282 34.3243243,33.6521739 C34.3243243,31.9953197 32.9811786,30.6521739 31.3243243,30.6521739 L9.32432432,30.6521739 Z" id="Combined-Shape"></path>
                </g>
            </g>
        </g>
    </g>
</svg>
`;

export const CardIconBackSVG = `
<svg id="CardIcon-svg" focusable="false" viewBox="0 0 80 54" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
    <g id="Desktop" transform="translate(-532.000000, -835.000000)" class="CardIcon-fill">
        <g id="card-new4" transform="translate(262.000000, 835.000000)">
            <g id="card-cvv-copy-2" transform="translate(270.000000, 0.000000)">
                <path d="M78,10 L7.10542736e-15,10 L6.21724894e-15,6 C5.81143669e-15,2.6862915 2.6862915,1.92604652e-14 6,1.86517468e-14 L72,1.86517468e-14 C75.3137085,1.80430284e-14 78,2.6862915 78,6 L78,10 Z M78,20 L78,46 C78,49.3137085 75.3137085,52 72,52 L6,52 C2.6862915,52 6.62306119e-15,49.3137085 6.21724894e-15,46 L6.21724894e-15,20 L78,20 Z M32.1833596,29.3104147 L30.910315,24 L35.3841575,24 L34.1474856,29.3104147 L38.1848556,25.7095171 L40.3308451,29.5286509 L35.0931759,30.9471864 L40.2944724,32.4748399 L38.0757375,36.257601 L34.1111129,32.5112126 L35.3841575,37.858 L30.910315,37.858 L32.2197323,32.5112126 L28.2551076,36.257601 L26.0363727,32.4748399 L31.2012966,30.9471864 L26,29.5286509 L28.1459895,25.7095171 L32.1833596,29.3104147 Z M49.5614523,29.3104147 L48.2884077,24 L52.7622502,24 L51.5255783,29.3104147 L55.5629484,25.7095171 L57.7089379,29.5286509 L52.4712686,30.9471864 L57.6725652,32.4748399 L55.4538303,36.257601 L51.4892056,32.5112126 L52.7622502,37.858 L48.2884077,37.858 L49.597825,32.5112126 L45.6332004,36.257601 L43.4144655,32.4748399 L48.5793893,30.9471864 L43.3780928,29.5286509 L45.5240823,25.7095171 L49.5614523,29.3104147 Z M65.9395451,29.3104147 L64.6665005,24 L69.140343,24 L67.9036711,29.3104147 L71.9410412,25.7095171 L74.0870307,29.5286509 L68.8493614,30.9471864 L74.0506579,32.4748399 L71.831923,36.257601 L67.8672984,32.5112126 L69.140343,37.858 L64.6665005,37.858 L65.9759178,32.5112126 L62.0112931,36.257601 L59.7925582,32.4748399 L64.9574821,30.9471864 L59.7561855,29.5286509 L61.902175,25.7095171 L65.9395451,29.3104147 Z" id="Combined-Shape"></path>
            </g>
        </g>
    </g>
  </g>
</svg>
`;

export const CardIconInvalidSVG = `
<svg id="CardIcon-svg" focusable="false" viewBox="0 0 80 54" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
        <g id="Desktop" transform="translate(-352.000000, -835.000000)" class="CardIcon-fill">
            <g id="card-new4" transform="translate(262.000000, 835.000000)">
                <g id="card-alert" transform="translate(90.000000, 0.000000)">
                    <path d="M78,22.3030518 C74.1884489,18.4134016 68.8760645,16 63,16 C59.5659038,16 56.3243314,16.8242926 53.4623461,18.2858147 C52.9202338,17.5085506 52.0195061,17 51,17 L43,17 C41.3431458,17 40,18.3431458 40,20 C40,21.6568542 41.3431458,23 43,23 L47.3472544,23 C44.021975,26.7152954 42,31.6215189 42,37 C42,42.8760645 44.4134016,48.1884489 48.3030518,52 L6,52 C2.6862915,52 1.37284885e-14,49.3137085 1.33226763e-14,46 L1.33226763e-14,6 C1.2916864e-14,2.6862915 2.6862915,1.4968968e-15 6,8.8817842e-16 L72,8.8817842e-16 C75.3137085,2.79460044e-16 78,2.6862915 78,6 L78,22.3030518 Z M9,17 C7.34314575,17 6,18.3431458 6,20 C6,21.6568542 7.34314575,23 9,23 L17,23 C18.6568542,23 20,21.6568542 20,20 C20,18.3431458 18.6568542,17 17,17 L9,17 Z M26,17 C24.3431458,17 23,18.3431458 23,20 C23,21.6568542 24.3431458,23 26,23 L34,23 C35.6568542,23 37,21.6568542 37,20 C37,18.3431458 35.6568542,17 34,17 L26,17 Z M9.32432432,30.6521739 C7.66747007,30.6521739 6.32432432,31.9953197 6.32432432,33.6521739 C6.32432432,35.3090282 7.66747007,36.6521739 9.32432432,36.6521739 L31.3243243,36.6521739 C32.9811786,36.6521739 34.3243243,35.3090282 34.3243243,33.6521739 C34.3243243,31.9953197 32.9811786,30.6521739 31.3243243,30.6521739 L9.32432432,30.6521739 Z" id="Combined-Shape"></path>
                    <path d="M62,20 L64,20 C72.836556,20 80,27.163444 80,36 L80,38 C80,46.836556 72.836556,54 64,54 L62,54 C53.163444,54 46,46.836556 46,38 L46,36 C46,27.163444 53.163444,20 62,20 Z M65.9466667,41.6466667 C65.8832,40.8850667 65.8832,40.5994667 65.8832,40.2504 C65.8832,39.7426667 65.9149333,39.0128 65.9784,37.7434667 L66.6130667,27.684 C66.74,25.9704 66.8352,24.6376 66.9938667,23.4 L59.6,23.4 C59.7269333,24.3837333 59.8856,26.0973333 59.9808,27.684 L60.6154667,37.7434667 C60.7424,39.6474667 60.7424,39.6474667 60.7424,39.9965333 C60.7424,40.7264 60.7424,40.9168 60.6789333,41.6466667 L65.9466667,41.6466667 Z M66.5178667,49.8021333 L66.5178667,43.8045333 L60.1394667,43.8045333 L60.1394667,49.8021333 L66.5178667,49.8021333 Z" id="Combined-Shape"></path>
                </g>
            </g>
        </g>
    </g>
</svg>
`;

export const CardTypeIcon = (cardType: string) => `
  <img id="CardIcon-svg-${cardType}" src="/v2/assets/${cardType}.svg"></img>
`;
